import { useEffect, useRef, useState } from 'react'
import type { BillingPlan } from '../../lib/api'
import { NEXT_PLAN, PLAN_LABEL, usageRatio, usageState } from '../../lib/planLimits'
import { IconArrowUpRight, IconCalendarClock, IconInfoCircle, IconMail } from '@tabler/icons-react'
import { useI18n } from '../../lib/i18n'
import type { Language } from '@webbuilder/i18n'

export type PlanUsageItem = {
  label: string
  used: number
  limit: number | null
  resetAt?: string | undefined
  timeZone?: string | undefined
}

const CONTACT_EMAIL = 'info@veltica.com'

function resetDateTime(resetAt: string, language: Language, timeZone = 'UTC'): string {
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  }
  try {
    return new Intl.DateTimeFormat(language, options).format(new Date(resetAt))
  } catch {
    return new Intl.DateTimeFormat(language, { ...options, timeZone: 'UTC' }).format(new Date(resetAt))
  }
}

export function PlanUsageSummary({ items, plan, onOpenPlans, limitOnly = false }: { items: PlanUsageItem[]; plan: BillingPlan; onOpenPlans: () => void; limitOnly?: boolean }) {
  const { t, fmt, language } = useI18n()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const rows = items.map((item) => ({ ...item, state: usageState(item.used, item.limit), ratio: usageRatio(item.used, item.limit) }))
  const anyFull = rows.some((r) => r.state === 'full')
  const anyWarn = rows.some((r) => r.state === 'warn')
  const next = NEXT_PLAN[plan]
  const limitLabel = (limit: number | null) => limit === null ? '∞' : fmt.number(limit)

  const accessibleLabel = items.map(({ label, used, limit }) => (
    limitOnly
      ? t('common.planUsage.limitLabel', { label, limit: limitLabel(limit) })
      : t('common.planUsage.usedLabel', { label, used: fmt.number(used), limit: limit === null ? t('common.planUsage.unlimited') : fmt.number(limit) })
  )).join('. ')

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const heading = anyFull
    ? t('common.planUsage.fullTitle')
    : anyWarn
      ? t('common.planUsage.warningTitle')
      : t('common.planUsage.title')

  return (
    <div
      ref={rootRef}
      className="dash-toolbar dash-layered-toolbar dash-plan-usage-toolbar"
      aria-label={accessibleLabel}
    >
      {rows.map(({ label, used, limit, state }) => (
        <span className={`dash-plan-usage-item is-${state}`} key={label} title={limitOnly ? t('common.planUsage.limitLabel', { label, limit: limitLabel(limit) }) : t('common.planUsage.usedLabel', { label, used: fmt.number(used), limit: limit === null ? t('common.planUsage.unlimited') : fmt.number(limit) })}>
          <span>{label}</span>
          <strong>{limitOnly ? limitLabel(limit) : t('common.planUsage.ofLimit', { used: fmt.number(used), limit: limitLabel(limit) })}</strong>
        </span>
      ))}
      <button
        className={`dash-plan-usage-info${anyFull ? ' is-full' : anyWarn ? ' is-warn' : ''}`}
        type="button"
        aria-label={t('common.planUsage.moreInfo')}
        data-tooltip={t('common.planUsage.tooltip')}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((current) => !current)}
      >
        <IconInfoCircle size={14} aria-hidden="true" />
      </button>

      {open ? (
        <div className="dash-plan-usage-popover" role="dialog" aria-label={t('common.planUsage.dialogAria')}>
          <strong>{heading}</strong>
          <ul className="dash-plan-usage-bars">
            {rows.map(({ label, used, limit, state, ratio }) => (
              <li key={label}>
                <span className="dash-plan-usage-bar-head">
                  <span>{label}</span>
                  <span className={`dash-plan-usage-bar-val is-${state}`}>{fmt.number(used)} / {limitLabel(limit)}</span>
                </span>
                <span className="dash-plan-usage-bar-track">
                  <span className={`dash-plan-usage-bar-fill is-${state}`} style={{ width: `${Math.min(100, Math.round(ratio * 100))}%` }} />
                </span>
              </li>
            ))}
          </ul>
          {rows.some((row) => row.resetAt) ? (
            <div className="dash-plan-usage-resets">
              {rows.filter((row) => row.resetAt).map((row) => (
                <span key={row.label}><IconCalendarClock size={14} aria-hidden="true" />{t('common.planUsage.resets', { date: resetDateTime(row.resetAt!, language, row.timeZone) })}</span>
              ))}
            </div>
          ) : null}
          <p>
            {anyFull
              ? t('common.planUsage.fullBody', { plan: PLAN_LABEL[plan] })
              : anyWarn
                ? t('common.planUsage.warningBody', { plan: PLAN_LABEL[plan] })
                : t('common.planUsage.okBody', { plan: PLAN_LABEL[plan] })}
          </p>
          {next ? (
            <button className="dash-primary-btn" type="button" onClick={() => { setOpen(false); onOpenPlans() }}>
              {t('common.planUsage.upgrade', { plan: PLAN_LABEL[next] })} <IconArrowUpRight size={14} />
            </button>
          ) : (
            <a className="dash-primary-btn" href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(t('common.planUsage.emailSubject'))}`}>
              <IconMail size={14} /> {t('common.planUsage.contact')}
            </a>
          )}
        </div>
      ) : null}
    </div>
  )
}
